import { Users, MapPin, Image, TrendingUp, TrendingDown, Activity, Calendar, FolderOpen } from 'lucide-react'
import { DashboardMetrics } from '@/types'

interface DashboardMetricsCardsProps {
  metrics: DashboardMetrics
}

export const DashboardMetricsCards = ({ metrics }: DashboardMetricsCardsProps) => {
  
  const metricCards = [
    {
      name: 'Total Users',
      value: metrics.totalUsers.value.toLocaleString(),
      change: metrics.totalUsers.change,
      changeType: metrics.totalUsers.changeType,
      icon: Users,
      color: 'blue' as const,
    },
    {
      name: 'Active Users Today',
      value: metrics.activeUsersToday.value.toLocaleString(),
      change: metrics.activeUsersToday.change,
      changeType: metrics.activeUsersToday.changeType,
      icon: Activity,
      color: 'green' as const,
    },
    {
      name: 'Total Events',
      value: metrics.totalEvents.value.toLocaleString(),
      change: metrics.totalEvents.change,
      changeType: metrics.totalEvents.changeType,
      icon: Calendar,
      color: 'orange' as const,
    },
    {
      name: 'Total Trips',
      value: metrics.totalTrips.value.toLocaleString(),
      change: metrics.totalTrips.change,
      changeType: metrics.totalTrips.changeType,
      icon: MapPin,
      color: 'purple' as const,
    },
    {
      name: 'Total Albums',
      value: metrics.totalAlbums.value.toLocaleString(),
      change: metrics.totalAlbums.change || 0,
      changeType: metrics.totalAlbums.changeType || 'increase',
      icon: FolderOpen,
      color: 'indigo' as const,
    },
    {
      name: 'Total Photos',
      value: metrics.totalPhotos.value.toLocaleString(),
      change: metrics.totalPhotos.change,
      changeType: metrics.totalPhotos.changeType,
      icon: Image,
      color: 'yellow' as const,
    },
  ]

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      orange: 'bg-orange-500',
      purple: 'bg-purple-500',
      indigo: 'bg-indigo-500',
      yellow: 'bg-yellow-500',
    }
    return colors[color as keyof typeof colors] || 'bg-gray-500'
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {metricCards.map((metric) => (
        <div key={metric.name} className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`inline-flex items-center justify-center p-3 rounded-md ${getColorClasses(metric.color)} text-white`}>
                  <metric.icon className="h-6 w-6" aria-hidden="true" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{metric.name}</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{metric.value}</div>
                    
                    {/* Change indicator */}
                    <div className="ml-2 flex items-baseline text-sm font-semibold">
                      {/* Show icon based on change type */}
                      {metric.changeType?.toLowerCase() === 'increase' && (
                        <TrendingUp className="self-center flex-shrink-0 h-4 w-4 text-green-500 mr-1" aria-hidden="true" />
                      )}
                      {metric.changeType?.toLowerCase() === 'decrease' && (
                        <TrendingDown className="self-center flex-shrink-0 h-4 w-4 text-red-500 mr-1" aria-hidden="true" />
                      )}
                      
                      {/* Show text with appropriate color */}
                      <span className={
                        metric.changeType?.toLowerCase() === 'increase' ? 'text-green-600' : 
                        metric.changeType?.toLowerCase() === 'decrease' ? 'text-red-600' : 'text-gray-600'
                      }>
                        {metric.changeType?.toLowerCase() === 'increase' ? '+' : ''}{metric.change.toFixed(1)}%
                      </span>
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
} 